import React from 'react'
import PropTypes from 'prop-types'

/**
 * Minimal markdown renderer for legal documents
 * Supports: headings, paragraphs, lists, bold, links, horizontal rules
 */
function MarkdownRenderer({ content }) {
  const renderMarkdown = (md) => {
    const lines = md.split('\n')
    const elements = []
    let currentList = null
    let currentListItems = []
    let inCodeBlock = false

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]

      // Skip code blocks
      if (line.trim().startsWith('```')) {
        inCodeBlock = !inCodeBlock
        continue
      }
      if (inCodeBlock) continue

      // Horizontal rule
      if (line.trim() === '---') {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        elements.push(<hr key={`hr-${i}`} />)
        continue
      }

      // Headings
      const h1Match = line.match(/^# (.+)$/)
      const h2Match = line.match(/^## (.+)$/)
      const h3Match = line.match(/^### (.+)$/)

      if (h1Match) {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        elements.push(<h1 key={`h1-${i}`}>{parseInline(h1Match[1])}</h1>)
        continue
      }

      if (h2Match) {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        elements.push(<h2 key={`h2-${i}`}>{parseInline(h2Match[1])}</h2>)
        continue
      }

      if (h3Match) {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        elements.push(<h3 key={`h3-${i}`}>{parseInline(h3Match[1])}</h3>)
        continue
      }

      // List items (unordered)
      const ulMatch = line.match(/^- (.+)$/)
      if (ulMatch) {
        if (currentList !== 'ul') {
          if (currentList) {
            elements.push(createList(currentList, currentListItems))
          }
          currentList = 'ul'
          currentListItems = []
        }
        currentListItems.push(ulMatch[1])
        continue
      }

      // Empty line - close list if open
      if (line.trim() === '') {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        continue
      }

      // Regular paragraph
      if (line.trim().length > 0) {
        if (currentList) {
          elements.push(createList(currentList, currentListItems))
          currentList = null
          currentListItems = []
        }
        elements.push(<p key={`p-${i}`}>{parseInline(line)}</p>)
      }
    }

    // Close any remaining list
    if (currentList) {
      elements.push(createList(currentList, currentListItems))
    }

    return elements
  }

  const createList = (type, items) => {
    const ListTag = type
    return (
      <ListTag key={`list-${Math.random()}`}>
        {items.map((item, idx) => (
          <li key={idx}>{parseInline(item)}</li>
        ))}
      </ListTag>
    )
  }

  const parseInline = (text) => {
    const parts = []
    let remaining = text
    let key = 0

    while (remaining.length > 0) {
      // Bold **text**
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/)
      if (boldMatch) {
        parts.push(
          <strong key={`bold-${key++}`}>{parseInline(boldMatch[1])}</strong>,
        )
        remaining = remaining.slice(boldMatch[0].length)
        continue
      }

      // Links [text](url)
      const linkMatch = remaining.match(/^\[(.+?)\]\((.+?)\)/)
      if (linkMatch) {
        parts.push(
          <a
            key={`link-${key++}`}
            href={linkMatch[2]}
            target={linkMatch[2].startsWith('http') ? '_blank' : undefined}
            rel={
              linkMatch[2].startsWith('http')
                ? 'noopener noreferrer'
                : undefined
            }
          >
            {linkMatch[1]}
          </a>,
        )
        remaining = remaining.slice(linkMatch[0].length)
        continue
      }

      // Regular text until next special char
      const nextSpecial = remaining.search(/[*[]/)
      if (nextSpecial === -1) {
        parts.push(remaining)
        break
      } else if (nextSpecial > 0) {
        parts.push(remaining.slice(0, nextSpecial))
        remaining = remaining.slice(nextSpecial)
      } else {
        // No match, consume one char
        parts.push(remaining[0])
        remaining = remaining.slice(1)
      }
    }

    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts
  }

  return <div className="markdown-content">{renderMarkdown(content)}</div>
}

MarkdownRenderer.propTypes = {
  content: PropTypes.string.isRequired,
}

export default MarkdownRenderer
